import { type Action, createAsyncThunk, type ThunkDispatch } from '@reduxjs/toolkit';
import type { IState } from 'redux/state';
import debug from 'utils/debug';
import { getIsTicServiceOnline, setTicServiceAsAvailable } from './tic.global';

const RETRY_MINS = 5;
/**
 * Set TIC service as unavailable, forcing TIC off at top-level `getHasEnabledTic` selector.
 * After RETRY_MINS mins, service is assumed available (will re-try in `TicContextProvider`)
 */
export const changeTicServiceOnlineState = createAsyncThunk<
  void,
  boolean,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>(
  'ticGlobal/changeTicServiceOnlineState',
  async (isOnline, { dispatch, getState }): Promise<void> => {
    // No-op if state is already correct
    if (isOnline === getIsTicServiceOnline(getState())) return;

    if (isOnline) {
      debug.template.infra('TIC service is now available');
      dispatch(setTicServiceAsAvailable(true));
      return;
    }

    // Side-effect: Forces TIC off
    dispatch(setTicServiceAsAvailable(false));
    debug.template.infra(`TIC service cannot be reached, retrying in ${RETRY_MINS}mins`);

    // Wait a while, then set service as available again (causing re-try)
    await new Promise(resolve => setTimeout(resolve, RETRY_MINS * 60 * 1000));

    debug.template.infra(`TIC service retrying after ${RETRY_MINS}mins`);
    dispatch(setTicServiceAsAvailable(true));
  }
);
